import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, UserRound } from 'lucide-react';
import { get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { humanize, todayIso } from '@/lib/utils';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, DatePicker, Field, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { useEmployeeOptions } from './employee-options';
import { rosterPath } from './NewRosterPage';
import { ChoiceButtons } from '@/components/ui/choice-buttons';

const WARD_BUTTONS_MAX = 5;

interface RosterWard {
  id: string;
  code: string;
  name: string;
}

const isoDate = (value: string | null) => (value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : todayIso());

const SHIFT_WINDOWS: Record<string, [string, string]> = {
  morning: ['08:00', '16:00'],
  evening: ['16:00', '00:00'],
  night: ['00:00', '08:00'],
};

export function AssignShiftPage() {
  const { rosterId = '' } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const returnTo = safeReturnTo(params.get('returnTo'), rosterPath(rosterId));
  const [initial] = useState(() => ({
    employeeId: params.get('employeeId') ?? '',
    shiftDate: isoDate(params.get('date')),
  }));
  const [employeeId, setEmployeeId] = useState(initial.employeeId);
  const [shiftDate, setShiftDate] = useState(initial.shiftDate);
  const [shift, setShift] = useState('morning');
  const [wardId, setWardId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const employees = useEmployeeOptions(true);
  const wards = useQuery({
    queryKey: ['roster-wards', rosterId],
    queryFn: () => get<RosterWard[]>(`/hr/rosters/${rosterId}/wards`),
    enabled: Boolean(rosterId),
  });
  const wardOptions = [
    { value: '', label: tr('admin', 'roster_ward_whole_department') },
    ...(wards.data ?? []).map((ward) => ({ value: ward.id, label: `${ward.code} · ${ward.name}` })),
  ];

  const assign = useMutation({
    mutationFn: () => {
      const [start, end] = SHIFT_WINDOWS[shift];
      const startsAt = new Date(`${shiftDate}T${start}:00`);
      const endsAt = new Date(`${shiftDate}T${end}:00`);
      if (endsAt <= startsAt) endsAt.setDate(endsAt.getDate() + 1);
      return post('/hr/roster-shifts', {
        rosterId,
        employeeId,
        shiftDate,
        shift,
        startsAt: startsAt.toISOString(),
        endsAt: endsAt.toISOString(),
        wardId: wardId || null,
      });
    },
    onSuccess: () => {
      toast.success('Shift assigned');
      void queryClient.invalidateQueries({ queryKey: ['roster', rosterId] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const dirty = !assign.isSuccess && (employeeId !== initial.employeeId || shift !== 'morning' || shiftDate !== initial.shiftDate || wardId !== '');

  return (
    <FormScreen
      title="Assign a shift"
      description="Overlapping shifts and approved leave are refused"
      icon={Clock}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!employeeId) return;
        setError(null);
        assign.mutate();
      }}
    >
      <FormSection title={tr('admin', 'person')} icon={UserRound} columns={1}>
        <Field label="Person" required>
          <Select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
            <option value="">Select…</option>
            {employees.data?.map((employee) => (
              <option key={employee.id} value={employee.id}>
                {employee.name} {employee.designation ? `· ${employee.designation}` : ''}
              </option>
            ))}
          </Select>
        </Field>
      </FormSection>

      <FormSection title={tr('admin', 'shift')} icon={Clock}>
        <Field label="Date" required>
          <DatePicker value={shiftDate} onChange={setShiftDate} />
        </Field>
        <Field label="Shift" required>
          <ChoiceButtons
            value={shift}
            onChange={setShift}
            options={['morning', 'evening', 'night'].map((s) => ({ value: s, label: `${humanize(s, 'shift')} (${SHIFT_WINDOWS[s][0]}–${SHIFT_WINDOWS[s][1]})` }))}
          />
        </Field>
        {wardOptions.length > 1 && (
          <Field label={tr('ipd', 'ward')} hint={tr('admin', 'roster_ward_hint')} className="sm:col-span-2">
            {wardOptions.length <= WARD_BUTTONS_MAX ? (
              <ChoiceButtons layout="chips" value={wardId} onChange={setWardId} options={wardOptions} label={tr('ipd', 'ward')} />
            ) : (
              <Select value={wardId} onChange={(e) => setWardId(e.target.value)}>
                {wardOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel="Assign" loading={assign.isPending} incomplete={!employeeId} cancelTo={returnTo} />
    </FormScreen>
  );
}
